import MarkerClusterer from '@google/markerclustererplus';
import { autorun } from 'mobx';
import i18n from '../i18n';
import { orderedColors, unorderedColors } from './schedules-colors';
import { filter, FILTERS } from '../store/filter';
import { routePage, SCHEDULES } from '../store/route-page';

const { SCHEDULE_0830, SCHEDULE_1000, SCHEDULE_1400, SCHEDULE_2400 } = SCHEDULES;

export const mapStyles = [
  { featureType: 'all', stylers: [{ saturation: -50 }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'road.highway', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'road.arterial', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'landscape', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.neighborhood', stylers: [{ visibility: 'off' }] },
];

const commonClustererStyle = { width: 50, height: 50, textColor: 'white', fontFamily: 'sans-serif', textSize: 16 };
export const clustererStyles = {
  unordered: {
    [SCHEDULE_0830]: [Object.assign({ url: '/images/clustering/u1.svg' }, commonClustererStyle)],
    [SCHEDULE_1000]: [Object.assign({ url: '/images/clustering/u2.svg' }, commonClustererStyle)],
    [SCHEDULE_1400]: [Object.assign({ url: '/images/clustering/u3.svg' }, commonClustererStyle)],
    [SCHEDULE_2400]: [Object.assign({ url: '/images/clustering/u4.svg' }, commonClustererStyle)],
  },
  ordered: {
    [SCHEDULE_0830]: [Object.assign({ url: '/images/clustering/o1.svg' }, commonClustererStyle)],
    [SCHEDULE_1000]: [Object.assign({ url: '/images/clustering/o2.svg' }, commonClustererStyle)],
    [SCHEDULE_1400]: [Object.assign({ url: '/images/clustering/o3.svg' }, commonClustererStyle)],
    [SCHEDULE_2400]: [Object.assign({ url: '/images/clustering/o4.svg' }, commonClustererStyle)],
  },
};

// second parameter numStyles not used
export function clustererStyleCalculator(markers) {
  const firstAddresses = markers.slice(0, 3).map(({ address: { street } }) => street).join('\n');
  const more = markers.length > 3 ? `\n+${markers.length - 3}` : '';
  const schedule = i18n.t(`schedules.${markers[0].address.schedule}`);
  const title = `${firstAddresses}${more}\n(${schedule})`;
  return {
    text: `+${markers.length}`,
    index: 0,
    title,
  };
}

export const markerIcon = ({ address }) => {
  const colors = address.order ? orderedColors : unorderedColors;
  const fillColor = colors[address.schedule] || colors.SCHEDULE_2400;
  return {
    fillColor,
    fillOpacity: 1.0,
    strokeColor: 'white',
    strokeOpacity: 1.0,
    path: 'm -15,0 a 15,15 0 1,0 30,0 a 15,15 0 1,0 -30,0',
  };
};

export const markerLabel = ({ address }) => {
  if (address.order) {
    const text = `${address.order}`;
    const color = 'white';
    const fontSizes = ['16px', '16px', '14px', '12px', '10px'];
    const fontSize = fontSizes[Math.min(text.length, fontSizes.length - 1)];
    return {
      text,
      color,
      fontFamily: 'sans-serif',
      fontSize,
    };
  }
  return null;
};

export const markerPosition = ({ address, fallbackPosition }) => {
  if (address.location) {
    const { coordinates: [lng, lat] } = address.location;
    return {
      lat,
      lng,
    };
  }
  return fallbackPosition;
};

export const markerTitle = ({ address }) => {
  const { uuid, street, postalCode, locality = '', schedule, notGeolocated } = address;
  if (notGeolocated) {
    return `# ${uuid}\n${street}\n${postalCode} ${locality}\n(${i18n.t(`schedules.${schedule}`)})\n(${i18n.t('help.list.notGeolocated')})`;
  }
  return `# ${uuid}\n${street}\n${postalCode} ${locality}\n(${i18n.t(`schedules.${schedule}`)})`;
};

export default class MapController {
  constructor() {
    this.initialized = false;
    this.update = this.update.bind(this);
    this.updateVisibility = this.updateVisibility.bind(this);
    this.onMarkerContextMenu = this.onMarkerContextMenu.bind(this);
    this.isMarkerVisible = this.isMarkerVisible.bind(this);
    this.reset();
  }

  reset() {
    this.map = undefined;
    this.overlay = undefined;
    this.markers = [];
    this.markerListenersArrayByKey = {};
    this.clusterers = { unordered: {}, ordered: {} };
    this.clustererListeners = [];
    this.filter = undefined;
    this.updateDisposer = undefined;
    this.updateVisibilityDisposer = undefined;
    this.onMarkerContextMenuCallback = undefined;
  }

  static onMarkerClick(marker) {
    if (marker.address.order === undefined) {
      routePage.orderAddresses({ keys: [marker.address.key] });
    } else {
      routePage.unorderAddresses({ keys: [marker.address.key] });
    }
  }

  static onClusterClick(cluster) {
    const unorderedKeys = cluster.getMarkers().filter(marker => marker.address.order === undefined).map(marker => marker.address.key);
    const orderedKeys = cluster.getMarkers().filter(marker => marker.address.order !== undefined).map(marker => marker.address.key);
    if (unorderedKeys.length > 0) {
      routePage.orderAddresses({ keys: unorderedKeys });
    } else if (orderedKeys.length > 0) {
      routePage.unorderAddresses({ keys: orderedKeys });
    }
  }

  onMarkerContextMenu(event, marker) {
    if (this.onMarkerContextMenuCallback !== undefined) {
      const { x, y } = this.overlay.getProjection().fromLatLngToContainerPixel(marker.getPosition());
      this.onMarkerContextMenuCallback(x, y, marker.address);
    }
  }

  // eslint-disable-next-line no-unused-vars
  setOnMarkerContextMenuCallback(callback = (x, y, address) => {}) {
    this.onMarkerContextMenuCallback = callback;
  }

  isMarkerVisible(address) {
    switch (this.filter) {
      case FILTERS.UNORDERED:
        return address.order === undefined;
      case FILTERS.ORDERED:
        return address.order !== undefined;
      case FILTERS.NOT_GEOLOCATED:
        return address.notGeolocated === true;
      case FILTERS.ALL:
      default:
        return true;
    }
  }

  init({ mapReference }) {
    if (!this.initialized) {
      this.reset();
      this.filter = filter.get();
      const allAddresses = [...routePage.unorderedAddresses, ...routePage.orderedAddresses];
      const markersOptions = allAddresses.map(address => ({
        position: markerPosition({ address }),
        title: markerTitle({ address }),
        draggable: false,
        icon: markerIcon({ address }),
        label: markerLabel({ address }),
        address: Object.assign({}, address),
        visible: this.isMarkerVisible(address),
      }));
      const bounds = new window.google.maps.LatLngBounds();
      markersOptions.forEach(markerOptions => bounds.extend(markerOptions.position));
      const center = bounds.isEmpty() ? { lat: 40.434294, lng: -3.702881 } : bounds.getCenter();
      // noinspection JSCheckFunctionSignatures
      this.map = new window.google.maps.Map(mapReference, {
        center,
        zoom: 15,
        disableDefaultUI: true,
        clickableIcons: false,
        mapTypeId: 'roadmap',
        styles: mapStyles,
      });
      if (!bounds.isEmpty()) {
        this.map.fitBounds(bounds, { top: 100, bottom: 50, left: 50, right: 50 });
      }
      this.overlay = new window.google.maps.OverlayView();
      this.overlay.draw = function draw() {};
      this.overlay.setMap(this.map);
      markersOptions.forEach((markerOptions) => {
        const { address } = markerOptions;
        const marker = new window.google.maps.Marker(Object.assign(markerOptions, { map: this.map }));
        marker.address = address;
        if (routePage.editable) {
          this.markerListenersArrayByKey = [
            marker.addListener('click', () => MapController.onMarkerClick(marker)),
            marker.addListener('rightclick', event => this.onMarkerContextMenu(event, marker)),
          ];
        }
        this.markers.push(marker);
      }, this);
      const unorderedMarkers = this.markers.filter(marker => marker.address.order === undefined);
      const orderedMarkers = this.markers.filter(marker => marker.address.order !== undefined);
      const markers = {
        unordered: {
          SCHEDULE_0830: unorderedMarkers.filter(marker => marker.address.schedule === SCHEDULE_0830),
          SCHEDULE_1000: unorderedMarkers.filter(marker => marker.address.schedule === SCHEDULE_1000),
          SCHEDULE_1400: unorderedMarkers.filter(marker => marker.address.schedule === SCHEDULE_1400),
          SCHEDULE_2400: unorderedMarkers.filter(marker => marker.address.schedule === SCHEDULE_2400),
        },
        ordered: {
          SCHEDULE_0830: orderedMarkers.filter(marker => marker.address.schedule === SCHEDULE_0830),
          SCHEDULE_1000: orderedMarkers.filter(marker => marker.address.schedule === SCHEDULE_1000),
          SCHEDULE_1400: orderedMarkers.filter(marker => marker.address.schedule === SCHEDULE_1400),
          SCHEDULE_2400: orderedMarkers.filter(marker => marker.address.schedule === SCHEDULE_2400),
        },
      };
      Object.entries(markers).forEach(([order, orderMarkers]) => Object.entries(orderMarkers).forEach(([schedule, scheduleMarkers]) => {
        const options = {
          styles: clustererStyles[order][schedule],
          calculator: clustererStyleCalculator,
          averageCenter: true,
          zoomOnClick: false,
          ignoreHidden: true,
        };
        this.clusterers[order][schedule] = new MarkerClusterer(this.map, scheduleMarkers, options);
        if (routePage.editable) {
          const listener = this.clusterers[order][schedule].addListener('click', MapController.onClusterClick);
          this.clustererListeners.push(listener);
        }
      }));
      this.updateDisposer = autorun(this.update);
      this.updateVisibilityDisposer = autorun(this.updateVisibility);
      this.initialized = true;
    }
  }

  update() {
    const oldAddressesByKey = {};
    this.markers.forEach(({ address }) => { oldAddressesByKey[address.key] = address; });
    const newAddressesByKey = {};
    // access route page here instead of inside the condition to ensure that mobx flags the access on the first run
    routePage.unorderedAddresses.forEach((address) => { newAddressesByKey[address.key] = address; });
    routePage.orderedAddresses.forEach((address) => { newAddressesByKey[address.key] = address; });
    if (this.initialized) {
      const oldKeys = new Set(Object.keys(oldAddressesByKey));
      const newKeys = new Set(Object.keys(newAddressesByKey));
      const keysToRemove = new Set([...oldKeys].filter(key => !newKeys.has(key)));
      const keysToAdd = new Set([...newKeys].filter(key => !oldKeys.has(key)));
      this.markers.filter(marker => keysToRemove.has(marker.address.key)).forEach((marker) => {
        this.markerListenersArrayByKey[marker.address.key].forEach(listener => listener.remove());
        delete this.markerListenersArrayByKey[marker.address.key];
      });
      const changedMarkersKeys = new Set();
      this.markers = this.markers.filter(marker => !keysToRemove.has(marker.address.key));
      this.markers.forEach((marker) => {
        const newAddress = newAddressesByKey[marker.address.key];
        const oldOrder = marker.address.order;
        const newOrder = newAddress.order;
        Object.assign(marker, { address: Object.assign({}, newAddress) });
        if (oldOrder !== newOrder) {
          if ((oldOrder !== undefined && newOrder === undefined) || (oldOrder === undefined && newOrder !== undefined)) {
            marker.setIcon(markerIcon({ address: newAddress }));
          }
          if (oldOrder !== undefined || newOrder !== undefined) {
            marker.setLabel(markerLabel({ address: newAddress }));
          }
        }
        const newTitle = markerTitle({ address: newAddress });
        if (newTitle !== marker.getTitle()) {
          marker.setTitle(newTitle);
        }
        const newVisible = this.isMarkerVisible(newAddress);
        if (newVisible !== marker.getVisible()) {
          marker.setVisible(newVisible);
          changedMarkersKeys.add(marker.address.key);
        }
        const oldPosition = marker.getPosition();
        const { location: { coordinates: [newLng, newLat] } } = newAddress;
        const newPosition = new window.google.maps.LatLng(newLat, newLng);
        if (!oldPosition.equals(newPosition)) {
          marker.setPosition(newPosition);
          changedMarkersKeys.add(marker.address.key);
        }
      });
      keysToAdd.forEach((key) => {
        const address = newAddressesByKey[key];
        // noinspection JSCheckFunctionSignatures
        const marker = new window.google.maps.Marker({
          map: this.map,
          position: markerPosition({ address }),
          title: markerTitle({ address }),
          draggable: false,
          icon: markerIcon({ address }),
          label: markerLabel({ address }),
          address: Object.assign({}, address),
          visible: this.isMarkerVisible(address),
        });
        marker.address = address;
        if (routePage.editable) {
          this.markerListenersArrayByKey[address.key] = [
            marker.addListener('click', () => MapController.onMarkerClick(marker)),
            marker.addListener('rightclick', event => this.onMarkerContextMenu(event, marker)),
          ];
        }
        this.markers.push(marker);
      });
      const unorderedMarkers = this.markers.filter(marker => marker.address.order === undefined);
      const orderedMarkers = this.markers.filter(marker => marker.address.order !== undefined);
      const markers = {
        unordered: {
          SCHEDULE_0830: unorderedMarkers.filter(marker => marker.address.schedule === SCHEDULE_0830),
          SCHEDULE_1000: unorderedMarkers.filter(marker => marker.address.schedule === SCHEDULE_1000),
          SCHEDULE_1400: unorderedMarkers.filter(marker => marker.address.schedule === SCHEDULE_1400),
          SCHEDULE_2400: unorderedMarkers.filter(marker => marker.address.schedule === SCHEDULE_2400),
        },
        ordered: {
          SCHEDULE_0830: orderedMarkers.filter(marker => marker.address.schedule === SCHEDULE_0830),
          SCHEDULE_1000: orderedMarkers.filter(marker => marker.address.schedule === SCHEDULE_1000),
          SCHEDULE_1400: orderedMarkers.filter(marker => marker.address.schedule === SCHEDULE_1400),
          SCHEDULE_2400: orderedMarkers.filter(marker => marker.address.schedule === SCHEDULE_2400),
        },
      };
      const clusterersToRepaint = [];
      Object.entries(this.clusterers).forEach(([order, clusterers]) => Object.entries(clusterers).forEach(([schedule, clusterer]) => {
        const newMarkers = markers[order][schedule];
        const newMarkersKeys = new Set(newMarkers.map(marker => marker.address.key));
        const oldMarkers = clusterer.getMarkers();
        const oldMarkersKeys = new Set(oldMarkers.map(marker => marker.address.key));
        const markersToRemove = oldMarkers.filter(marker => !newMarkersKeys.has(marker.address.key));
        const markersToAdd = newMarkers.filter(marker => !oldMarkersKeys.has(marker.address.key));
        if (markersToRemove.length > 0) {
          clusterer.removeMarkers(markersToRemove, true);
        }
        if (markersToAdd.length > 0) {
          clusterer.addMarkers(markersToAdd, true);
        }
        if (markersToRemove.length > 0 || markersToAdd.length > 0
          || clusterer.getMarkers().some(marker => changedMarkersKeys.has(marker.address.key))) {
          clusterersToRepaint.push(clusterer);
        }
      }));
      clusterersToRepaint.forEach(clusterer => clusterer.repaint());
    }
  }

  fitMapToVisibleMarkers() {
    if (this.initialized) {
      const bounds = new window.google.maps.LatLngBounds();
      this.markers.filter(marker => marker.getVisible()).forEach(marker => bounds.extend(marker.position));
      if (!bounds.isEmpty()) {
        this.map.fitBounds(bounds, { top: 100, bottom: 50, left: 50, right: 50 });
      }
    }
  }

  updateVisibility() {
    this.filter = filter.get();
    if (this.initialized) {
      this.markers.forEach(marker => marker.setVisible(this.isMarkerVisible(marker.address)));
      Object.values(this.clusterers).forEach(clusterers => Object.values(clusterers).forEach(clusterer => clusterer.repaint()));
    }
  }

  finalize() {
    if (this.initialized) {
      Object.values(this.markerListenersArrayByKey).forEach(listenerArray => listenerArray.forEach(listener => listener.remove()));
      this.clustererListeners.forEach(listener => listener.remove());
      Object.entries(this.clusterers).forEach(([, orderClusterers]) => Object.entries(orderClusterers).forEach(([, clusterer]) => {
        clusterer.clearMarkers();
      }));
      this.markers.forEach(marker => marker.setMap(null));
      this.updateDisposer();
      this.updateVisibilityDisposer();
      this.reset();
      this.initialized = false;
    }
  }
}



// WEBPACK FOOTER //
// ./src/helpers/map.js